import { Component, Inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { AppSettings } from '../../../app.settings';
import { SnackBarComponent } from '../../snackbar/snackbar.component';
import { ManagedFeatureFlagsService } from '../../../services/managed-feature-flags/managed-feature-flags.service';
import { UsersService } from '../../../services/users/users.service';
import { PAYROLL_ADMIN_COMPANY_THEMING_FLAG } from '../payroll-admin-feature-flags';
import { normalizeLogoUrl } from '../../../helpers/logo-url.helper';

export interface PayrollCompanyDialogData {
  company?: any;
}

export interface PayrollCompanyDialogResult {
  name: string;
  EIN: string;
  SIC: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postcode: string;
  metadata: any;
  imageData: ArrayBuffer | null;
}

interface UsAddressSuggestion {
  display: string;
  address1: string;
  city: string;
  state: string;
  postcode: string;
}

const THEME_COLOR_PATTERN = /^#(?:[0-9A-Fa-f]{3}){1,2}$/;
const COMPANY_THEME_KEYS = [
  'ourlyPrimaryColor',
  'ourlySecondaryColor',
  'wallitTertiaryColor',
  'wallitGradientNEColor',
  'wallitGradientSWColor',
] as const;

type CompanyThemeKey = typeof COMPANY_THEME_KEYS[number];

@Component({
    selector: 'app-payroll-company-dialog',
    templateUrl: './payroll-company-dialog.component.html',
    styleUrls: ['./payroll-company-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PayrollCompanyDialogComponent implements OnDestroy {
  protected readonly form;
  protected isCompanyThemingFeatureEnabled = false;

  protected imageData: ArrayBuffer | null = null;
  protected selectedFileName = '';
  protected previewUrl = '';
  protected legalAddressSuggestions: UsAddressSuggestion[] = [];
  protected headOfficeAddressSuggestions: UsAddressSuggestion[] = [];
  protected legalAddressQuery = '';
  protected headOfficeAddressQuery = '';
  protected isLegalAddressLoading = false;
  protected isHeadOfficeAddressLoading = false;
  protected legalAddressAutocompleteError = '';
  protected headOfficeAddressAutocompleteError = '';

  private readonly company: any;
  private readonly companyMetadata: any;
  private readonly subscriptions = new Subscription();
  private readonly usAddressSearchUrl = `${AppSettings.getEndpoint()}radar/geocode/free/forward`;
  private selectedLegalAddressKey = '';
  private selectedHeadOfficeAddressKey = '';

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private managedFeatureFlagsService: ManagedFeatureFlagsService,
    private usersService: UsersService,
    private dialogRef: MatDialogRef<PayrollCompanyDialogComponent, PayrollCompanyDialogResult | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: PayrollCompanyDialogData
  ) {
    this.company = data?.company || {};
    this.companyMetadata = {
      ...(this.getPayrollConfig(this.company)?.payrollCompany || {}),
      ...(this.company?.metadata?.payrollCompany || {}),
    };
    const companyTheme = this.getCompanyTheme(this.companyMetadata?.theme);
    this.previewUrl = normalizeLogoUrl(this.company?.logoUrl);
    this.form = this.fb.group({
      name: [this.company?.name || '', [Validators.required]],
      dba: [this.companyMetadata?.dba || ''],
      EIN: [this.company?.EIN || '', [Validators.required]],
      SIC: [this.company?.SIC || ''],
      address1: [this.company?.address1 || '', [Validators.required]],
      address2: [this.company?.address2 || ''],
      city: [this.company?.city || '', [Validators.required]],
      state: [this.company?.state || '', [Validators.required]],
      postcode: [this.company?.postcode || '', [Validators.required]],
      headOfficeSameAsLegal: [this.companyMetadata?.headOfficeSameAsLegal !== false],
      headOfficeAddress1: [this.companyMetadata?.headOffice?.address1 || this.company?.address1 || '', [Validators.required]],
      headOfficeAddress2: [this.companyMetadata?.headOffice?.address2 || this.company?.address2 || ''],
      headOfficeCity: [this.companyMetadata?.headOffice?.city || this.company?.city || '', [Validators.required]],
      headOfficeState: [this.companyMetadata?.headOffice?.state || this.company?.state || '', [Validators.required]],
      headOfficePostcode: [this.companyMetadata?.headOffice?.postcode || this.company?.postcode || '', [Validators.required]],
      contactName: [this.companyMetadata?.contact?.name || '', [Validators.required]],
      contactTitle: [this.companyMetadata?.contact?.title || ''],
      contactEmail: [this.companyMetadata?.contact?.email || '', [Validators.required, Validators.email]],
      contactPhone: [this.companyMetadata?.contact?.phone || ''],
      useCustomTheme: [!!companyTheme],
      ourlyPrimaryColor: [companyTheme?.ourlyPrimaryColor || ''],
      ourlySecondaryColor: [companyTheme?.ourlySecondaryColor || ''],
      wallitTertiaryColor: [companyTheme?.wallitTertiaryColor || ''],
      wallitGradientNEColor: [companyTheme?.wallitGradientNEColor || ''],
      wallitGradientSWColor: [companyTheme?.wallitGradientSWColor || ''],
    });

    this.bindHeadOfficeToLegalAddress();
    this.bindAddressAutocomplete();
    this.bindThemeControls();
    void this.loadCompanyThemingFeatureFlag();
  }

  private getPayrollConfig(company: any): any {
    const rawPayrollConfig = company?.payrollConfig;
    if (!rawPayrollConfig) {
      return {};
    }

    if (typeof rawPayrollConfig === 'string') {
      try {
        return JSON.parse(rawPayrollConfig);
      } catch (_) {
        return {};
      }
    }

    return typeof rawPayrollConfig === 'object' ? rawPayrollConfig : {};
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewUrl);
    }
  }

  protected displayAddressSuggestion(option: UsAddressSuggestion | string | null): string {
    if (!option) {
      return '';
    }
    return typeof option === 'string' ? option : option.display;
  }

  protected onLegalAddressSelected(event: MatAutocompleteSelectedEvent): void {
    this.applyAddressSelection(event.option.value as UsAddressSuggestion, false);
  }

  protected onHeadOfficeAddressSelected(event: MatAutocompleteSelectedEvent): void {
    this.applyAddressSelection(event.option.value as UsAddressSuggestion, true);
  }

  protected close(): void {
    this.dialogRef.close(undefined);
  }

  protected onChooseFile(fileInput: HTMLInputElement): void {
    fileInput.click();
  }

  protected onLogoSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) {
      return;
    }

    if (file.type.indexOf('image') < 0) {
      this.snackBar.openFromComponent(SnackBarComponent, { data: 'You did not select a valid image file' });
      return;
    }

    this.selectedFileName = file.name;
    const reader = new FileReader();
    reader.onload = () => {
      const buffer = reader.result as ArrayBuffer;
      this.imageData = buffer;
      if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.previewUrl);
      }
      this.previewUrl = URL.createObjectURL(new Blob([buffer], { type: file.type }));
    };
    reader.readAsArrayBuffer(file);
  }

  protected clearLogoPreview(): void {
    if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewUrl);
    }
    this.previewUrl = '';
  }

  protected save(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    const values = this.form.getRawValue();
    const payrollCompanyMetadata: Record<string, any> = {
      dba: this.toTrimmedString(values.dba),
      headOfficeSameAsLegal: !!values.headOfficeSameAsLegal,
      headOffice: {
        address1: this.toTrimmedString(values.headOfficeAddress1),
        address2: this.toTrimmedString(values.headOfficeAddress2),
        city: this.toTrimmedString(values.headOfficeCity),
        state: this.toTrimmedString(values.headOfficeState),
        postcode: this.toTrimmedString(values.headOfficePostcode),
      },
      contact: {
        name: this.toTrimmedString(values.contactName),
        title: this.toTrimmedString(values.contactTitle),
        email: this.toTrimmedString(values.contactEmail),
        phone: this.toTrimmedString(values.contactPhone),
      },
    };

    if (this.isCompanyThemingFeatureEnabled) {
      payrollCompanyMetadata.theme = values.useCustomTheme ? this.buildThemeFromFormValues(values) : null;
    }

    this.dialogRef.close({
      name: this.toTrimmedString(values.name),
      EIN: this.toTrimmedString(values.EIN),
      SIC: this.toTrimmedString(values.SIC),
      address1: this.toTrimmedString(values.address1),
      address2: this.toTrimmedString(values.address2),
      city: this.toTrimmedString(values.city),
      state: this.toTrimmedString(values.state),
      postcode: this.toTrimmedString(values.postcode),
      metadata: {
        payrollCompany: payrollCompanyMetadata,
      },
      imageData: this.imageData,
    });
  }

  protected get isEditMode(): boolean {
    return !!this.company?.id;
  }

  private bindHeadOfficeToLegalAddress(): void {
    const legalToHeadMap = [
      ['address1', 'headOfficeAddress1'],
      ['address2', 'headOfficeAddress2'],
      ['city', 'headOfficeCity'],
      ['state', 'headOfficeState'],
      ['postcode', 'headOfficePostcode'],
    ] as const;

    const setHeadOfficeControlState = (sameAsLegal: boolean): void => {
      if (sameAsLegal) {
        legalToHeadMap.forEach(([legalKey, headKey]) => {
          this.form.get(headKey)?.setValue(this.form.get(legalKey)?.value || '', { emitEvent: false });
          this.form.get(headKey)?.disable({ emitEvent: false });
        });
      } else {
        legalToHeadMap.forEach(([, headKey]) => {
          this.form.get(headKey)?.enable({ emitEvent: false });
        });
      }
    };

    setHeadOfficeControlState(!!this.form.get('headOfficeSameAsLegal')?.value);

    let previousSameAsLegal = !!this.form.get('headOfficeSameAsLegal')?.value;
    this.form.get('headOfficeSameAsLegal')?.valueChanges.subscribe(value => {
      const nextSameAsLegal = !!value;
      setHeadOfficeControlState(nextSameAsLegal);

      if (previousSameAsLegal && !nextSameAsLegal) {
        legalToHeadMap.forEach(([, headKey]) => {
          this.form.get(headKey)?.setValue('', { emitEvent: false });
        });
        this.headOfficeAddressSuggestions = [];
        this.headOfficeAddressAutocompleteError = '';
        this.selectedHeadOfficeAddressKey = '';
      } else if (nextSameAsLegal) {
        this.selectedHeadOfficeAddressKey = this.toTrimmedString(this.form.get('headOfficeAddress1')?.value);
      }

      previousSameAsLegal = nextSameAsLegal;
    });

    legalToHeadMap.forEach(([legalKey, headKey]) => {
      this.form.get(legalKey)?.valueChanges.subscribe(value => {
        if (this.form.get('headOfficeSameAsLegal')?.value) {
          this.form.get(headKey)?.setValue(value || '', { emitEvent: false });
        }
      });
    });
  }

  private bindThemeControls(): void {
    this.updateThemeControlState();
    this.form.get('useCustomTheme')?.valueChanges.subscribe(() => {
      this.updateThemeControlState();
    });
  }

  private async loadCompanyThemingFeatureFlag(): Promise<void> {
    try {
      this.isCompanyThemingFeatureEnabled = await this.managedFeatureFlagsService.isEnabled(
        PAYROLL_ADMIN_COMPANY_THEMING_FLAG,
        'team-admin',
        {
          userId: this.usersService.getCurrentUserId() || undefined,
        },
      );
    } catch (error) {
      console.error('Failed to evaluate payroll company theming feature flag', error);
      this.isCompanyThemingFeatureEnabled = false;
    } finally {
      this.updateThemeControlState();
    }
  }

  private bindAddressAutocomplete(): void {
    this.subscriptions.add(
      this.form
        .get('address1')
        ?.valueChanges.pipe(
          map(value => this.toTrimmedString(value)),
          map(value => {
            this.legalAddressQuery = value;
            if (value !== this.selectedLegalAddressKey) {
              this.selectedLegalAddressKey = '';
            }
            if (value.length < 3) {
              this.legalAddressSuggestions = [];
              this.isLegalAddressLoading = false;
              this.legalAddressAutocompleteError = '';
            }
            return value;
          }),
          debounceTime(300),
          distinctUntilChanged(),
          switchMap(value => {
            if (value.length < 3) {
              return of({ results: [], error: '' });
            }

            this.isLegalAddressLoading = true;
            this.legalAddressAutocompleteError = '';
            return this.searchUsAddresses(value).pipe(
              map(results => ({ results, error: '' })),
              catchError(() => of({ results: [], error: 'Autocomplete is temporarily unavailable' }))
            );
          })
        )
        .subscribe(({ results, error }: { results: UsAddressSuggestion[]; error: string }) => {
          this.isLegalAddressLoading = false;
          this.legalAddressAutocompleteError = error || '';
          this.legalAddressSuggestions = results;
        })
    );

    this.subscriptions.add(
      this.form
        .get('headOfficeAddress1')
        ?.valueChanges.pipe(
          map(value => this.toTrimmedString(value)),
          map(value => {
            this.headOfficeAddressQuery = value;
            if (value !== this.selectedHeadOfficeAddressKey) {
              this.selectedHeadOfficeAddressKey = '';
            }
            if (value.length < 3) {
              this.headOfficeAddressSuggestions = [];
              this.isHeadOfficeAddressLoading = false;
              this.headOfficeAddressAutocompleteError = '';
            }
            return value;
          }),
          debounceTime(300),
          distinctUntilChanged(),
          switchMap(value => {
            if (value.length < 3) {
              return of({ results: [], error: '' });
            }

            this.isHeadOfficeAddressLoading = true;
            this.headOfficeAddressAutocompleteError = '';
            return this.searchUsAddresses(value).pipe(
              map(results => ({ results, error: '' })),
              catchError(() => of({ results: [], error: 'Autocomplete is temporarily unavailable' }))
            );
          })
        )
        .subscribe(({ results, error }: { results: UsAddressSuggestion[]; error: string }) => {
          this.isHeadOfficeAddressLoading = false;
          this.headOfficeAddressAutocompleteError = error || '';
          this.headOfficeAddressSuggestions = results;
        })
    );
  }

  private searchUsAddresses(query: string) {
    if (!query || query.length < 3) {
      return of([]);
    }

    return this.http.get<any>(`${this.usAddressSearchUrl}/${encodeURIComponent(query)}`).pipe(
      map(response => this.parseSuggestions(response))
    );
  }

  private parseSuggestions(response: any): UsAddressSuggestion[] {
    const radarAddresses = Array.isArray(response?.addresses) ? response.addresses : [];
    if (radarAddresses.length > 0) {
      return this.normalizeSuggestions(
        radarAddresses
          .filter((item: any) => {
            const countryCode = this.toTrimmedString(item?.countryCode).toUpperCase();
            return !countryCode || countryCode === 'US';
          })
          .slice(0, 8)
          .map((item: any) => {
            const address1 = this.buildRadarAddressLine1(item);
            return {
              display: this.toTrimmedString(item?.formattedAddress || address1),
              address1,
              city: this.toTrimmedString(item?.city),
              state: this.toTrimmedString(item?.stateCode || item?.state),
              postcode: this.toTrimmedString(item?.postalCode),
            };
          })
      );
    }

    // Backward compatibility for census-style payloads.
    const censusMatches = Array.isArray(response?.result?.addressMatches) ? response.result.addressMatches : [];
    return this.normalizeSuggestions(
      censusMatches.slice(0, 8).map((item: any) => {
        const components = item?.addressComponents || {};
        const address1 = this.buildCensusAddressLine1(components, item?.matchedAddress);
        return {
          display: this.toTrimmedString(item?.matchedAddress || address1),
          address1,
          city: this.toTrimmedString(components.city),
          state: this.toTrimmedString(components.state),
          postcode: this.toTrimmedString(components.zip),
        };
      })
    );
  }

  private normalizeSuggestions(suggestions: UsAddressSuggestion[]): UsAddressSuggestion[] {
    const seen = new Set<string>();

    return suggestions
      .filter((item: UsAddressSuggestion) => {
        return !!(
          this.toTrimmedString(item?.address1) &&
          this.toTrimmedString(item?.city) &&
          this.toTrimmedString(item?.state) &&
          this.toTrimmedString(item?.postcode)
        );
      })
      .filter((item: UsAddressSuggestion) => {
        const key = [
          this.toTrimmedString(item?.address1).toLowerCase(),
          this.toTrimmedString(item?.city).toLowerCase(),
          this.toTrimmedString(item?.state).toLowerCase(),
          this.toTrimmedString(item?.postcode).toLowerCase(),
        ].join('|');

        if (!key || seen.has(key)) {
          return false;
        }

        seen.add(key);
        return true;
      })
      .slice(0, 8);
  }

  private extractAddressLine1(address: string): string {
    const trimmedAddress = this.toTrimmedString(address);
    if (!trimmedAddress) {
      return '';
    }
    return this.toTrimmedString(trimmedAddress.split(',')[0]);
  }

  private buildCensusAddressLine1(components: any, fallbackAddress: string): string {
    const fallbackLine1 = this.extractAddressLine1(fallbackAddress);
    if (fallbackLine1) {
      return fallbackLine1;
    }

    const fromAddress = this.toTrimmedString(components?.fromAddress);
    const preDirection = this.toTrimmedString(components?.preDirection);
    const preType = this.toTrimmedString(components?.preType);
    const streetName = this.toTrimmedString(components?.streetName);
    const suffixType = this.toTrimmedString(components?.suffixType);
    const suffixDirection = this.toTrimmedString(components?.suffixDirection);
    const suffixQualifier = this.toTrimmedString(components?.suffixQualifier);

    const address1 = [fromAddress, preDirection, preType, streetName, suffixType, suffixDirection, suffixQualifier]
      .filter(part => !!part)
      .join(' ')
      .trim();

    if (address1) {
      return address1;
    }

    return '';
  }

  private buildRadarAddressLine1(components: any): string {
    const fromFormattedAddress = this.extractAddressLine1(this.toTrimmedString(components?.formattedAddress));
    if (fromFormattedAddress) {
      return fromFormattedAddress;
    }

    const number = this.toTrimmedString(components?.number);
    const street = this.toTrimmedString(components?.street || components?.street1);
    const unit = this.toTrimmedString(components?.unit);
    const address1 = [number, street, unit]
      .filter(part => !!part)
      .join(' ')
      .trim();
    return address1;
  }

  private applyAddressSelection(selection: UsAddressSuggestion, isHeadOffice: boolean): void {
    if (!selection) {
      return;
    }

    if (isHeadOffice) {
      this.form.patchValue(
        {
          headOfficeAddress1: selection.address1,
          headOfficeCity: selection.city,
          headOfficeState: selection.state,
          headOfficePostcode: selection.postcode,
        },
        { emitEvent: false }
      );
      this.headOfficeAddressSuggestions = [];
      this.headOfficeAddressAutocompleteError = '';
      this.isHeadOfficeAddressLoading = false;
      this.selectedHeadOfficeAddressKey = selection.address1;
      return;
    }

    this.form.patchValue(
      {
        address1: selection.address1,
        city: selection.city,
        state: selection.state,
        postcode: selection.postcode,
      },
      { emitEvent: false }
    );
    this.legalAddressSuggestions = [];
    this.legalAddressAutocompleteError = '';
    this.isLegalAddressLoading = false;
    this.selectedLegalAddressKey = selection.address1;

    if (this.form.get('headOfficeSameAsLegal')?.value) {
      this.form.patchValue(
        {
          headOfficeAddress1: selection.address1,
          headOfficeCity: selection.city,
          headOfficeState: selection.state,
          headOfficePostcode: selection.postcode,
        },
        { emitEvent: false }
      );
      this.selectedHeadOfficeAddressKey = selection.address1;
    }
  }

  private updateThemeControlState(): void {
    const shouldRequireTheme = this.isCompanyThemingFeatureEnabled && !!this.form.get('useCustomTheme')?.value;
    COMPANY_THEME_KEYS.forEach((key) => {
      const control = this.form.get(key);
      if (!control) {
        return;
      }

      if (shouldRequireTheme) {
        control.setValidators([Validators.required, Validators.pattern(THEME_COLOR_PATTERN)]);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  private buildThemeFromFormValues(values: Record<string, any>): Record<CompanyThemeKey, string> {
    return COMPANY_THEME_KEYS.reduce((theme, key) => {
      theme[key] = this.toTrimmedString(values[key]);
      return theme;
    }, {} as Record<CompanyThemeKey, string>);
  }

  private getCompanyTheme(theme: any): Record<CompanyThemeKey, string> | null {
    if (!theme || typeof theme !== 'object') {
      return null;
    }

    const normalizedTheme = COMPANY_THEME_KEYS.reduce((result, key) => {
      result[key] = this.toTrimmedString(theme[key]);
      return result;
    }, {} as Record<CompanyThemeKey, string>);

    const hasTheme = COMPANY_THEME_KEYS.every((key) => !!normalizedTheme[key]);
    return hasTheme ? normalizedTheme : null;
  }

  protected get useCustomTheme(): boolean {
    return !!this.form.get('useCustomTheme')?.value;
  }

  protected hasThemeError(key: CompanyThemeKey, errorKey: 'required' | 'pattern'): boolean {
    const control = this.form.get(key);
    return !!(
      this.isCompanyThemingFeatureEnabled &&
      this.useCustomTheme &&
      control?.hasError(errorKey) &&
      control?.touched
    );
  }

  protected getThemeControl(key: CompanyThemeKey): AbstractControl | null {
    return this.form.get(key);
  }

  private toTrimmedString(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value).trim();
  }
}
